import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./style.css";
import { Icon } from "@iconify/react";
import { translate } from "@/utils/langUtils";
import { REQUEST_STATUS } from "@/Data/constants";
import { STATUS_MAP } from "@/Data/status";
import { FormateDate, toolTipAtrributes } from "@/utils";

interface FlowStep {
  key: number;
  label: string;
}

interface IRequestFlow {
  statusesLog: any[];
  steps: FlowStep[];
  currentStep: number;
}

const SNAKE_MIN = 340;
const SNAKE_MID = 700;
const SNAKE_MAX = 1700;
const SNAKE_COLS_SMALL = 3;
const SNAKE_COLS_LARGE = 5;

const RequestFlow: React.FC<IRequestFlow> = ({
  statusesLog,
  steps,
  currentStep,
}) => {

  const { i18n, t } = useTranslation();
  const isRTL =
    (typeof i18n.dir === "function" && i18n.dir() === "rtl") ||
    (i18n.language?.toLowerCase?.().startsWith("ar") ?? false);
  const [isSnakeMode, setIsSnakeMode] = useState(true);
  const [snakeColumns, setSnakeColumns] = useState<number>(SNAKE_COLS_SMALL);

  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  useEffect(() => {
    const updateMode = () => {
      const width = window.innerWidth;
      const enabled = width >= SNAKE_MIN && width <= SNAKE_MAX;
      setIsSnakeMode(enabled);
      setSnakeColumns(width >= SNAKE_MID && width <= SNAKE_MAX ? SNAKE_COLS_LARGE : SNAKE_COLS_SMALL);
    };

    updateMode();
    window.addEventListener("resize", updateMode);
    return () => window.removeEventListener("resize", updateMode);
  }, []);

  return (
    <div
      className={`cr-flow ${isRTL ? "rtl" : ""} ${isSnakeMode ? "snake-mode" : ""}`}
      style={isSnakeMode ? ({ ["--cr-flow-cols" as any]: snakeColumns } as React.CSSProperties) : undefined}
    >
      {steps.map((step, index) => {
        const cols = isSnakeMode ? snakeColumns : SNAKE_COLS_SMALL;
        const row = Math.floor(index / cols);
        const col = index % cols;
        const isRowReversed = (r: number) => (isRTL ? r % 2 === 0 : r % 2 !== 0);

        const snakeCol = isRowReversed(row) ? cols - col : col + 1;

        const isDone = !!statusesLog?.find((status) => status.id == step.key)?.id && currentStep != statusesLog?.find((status) => status.id == step.key)?.id;
        const isCancelled = !statusesLog?.find((status) => status.id == step.key)?.id && currentStep == STATUS_MAP.RequestClosed;
        const isRequestCompleted = currentStep == STATUS_MAP?.TreatmentCompleted;
        const isActive = index === currentIndex;
        const hasNext = index < steps.length - 1;
        const nextRow = Math.floor((index + 1) / cols);
        const isNextInSameRow = hasNext && nextRow === row;

        const nextCol = index + 1 < steps.length ? (index + 1) % cols : 0;
        const nextIsRowReversed = isRowReversed(nextRow);
        const nextSnakeCol = index + 1 < steps.length
          ? (nextIsRowReversed ? cols - nextCol : nextCol + 1)
          : snakeCol;

        // Determine direction based on *actual* placed columns (RTL-safe)
        const connectorClass = isSnakeMode
          ? isNextInSameRow
            ? nextSnakeCol > snakeCol
              ? isRTL ? "to-left" : "to-right"
              : isRTL ? "to-right" : "to-left"
            : "to-next-row"
          : isRTL
            ? "to-left"
            : "to-right";

        const shouldRenderConnector = hasNext;

        return (
          <div
            key={step.key}
            className={`cr-flow-item ${isCancelled && !isActive ? "cancelled" : ""}`}
            style={isSnakeMode ? { gridColumn: snakeCol, gridRow: row + 1 } : undefined}
          >
            <div className="cr-flow-top">

              <div
                {...toolTipAtrributes(isCancelled && !isActive ?
                  t("cancelled before reaching it").toLocaleUpperCase() :
                  !isCancelled && !isDone && !isActive ? t("did not reach it").toLocaleUpperCase() :
                    FormateDate(statusesLog.find((status) => status.id == step.key)?.date), "top")}
                className={`cr-flow-dot ${isDone || currentStep == STATUS_MAP.RequestClosed || isRequestCompleted ? "done" : ""} ${isActive && currentStep != STATUS_MAP.RequestClosed  && !isRequestCompleted? "active" : ""}`}>
                {isActive && currentStep != STATUS_MAP.RequestClosed && !isRequestCompleted && (
                  <Icon color="white" icon="vaadin:dot-circle" width={18} height={18} />
                )}
                {isCancelled && !isActive && (
                  <Icon color="white" icon="ic:round-cancel" width={18} height={18} />
                )}
              </div>

              {shouldRenderConnector && connectorClass && (
                <div className={`cr-flow-line ${connectorClass} ${isDone ? "done" : ""}`} />
              )}

            </div>

            <div className={`cr-flow-label ${isActive ? "active" : ""}`}>
              {translate(step.label)?.toUpperCase()}
            </div>

          </div>
        );
      })}
    </div>
  );
};

export default RequestFlow;